import { ReactNode, useEffect, useMemo, useState } from "react"
// eslint-disable-next-line no-restricted-imports -- React hook rendering UI via pushDialog
import { useTranslation } from "react-i18next"

import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { pushDialog } from "@/store/dialogs-state"

export const FALLBACK_MIN_CHROME_VERSION = 140

const VERSION_HISTORY_API_BASE = "https://versionhistory.googleapis.com/v1/chrome/platforms"

type ChromePlatformSegment = "win" | "win64" | "mac" | "linux"

type Platform = "windows" | "mac" | "linux" | "chromeos" | "other"

interface NavigatorUADataBrand {
	brand: string
	version: string
}

interface NavigatorUAData {
	platform?: string
	brands?: NavigatorUADataBrand[]
	getHighEntropyValues?: (
		hints: string[],
	) => Promise<{ fullVersionList?: NavigatorUADataBrand[] }>
}

interface NavigatorWithUAData extends Navigator {
	userAgentData?: NavigatorUAData
}

const hasUserAgentData = (nav: Navigator): nav is NavigatorWithUAData => "userAgentData" in nav

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null

const isAutomatedBrowser = () => {
	if (typeof navigator === "undefined") {
		return false
	}
	return navigator.webdriver || /HeadlessChrome/i.test(navigator.userAgent)
}

const detectChromePlatform = (): ChromePlatformSegment => {
	if (typeof navigator === "undefined") {
		return "mac"
	}

	const userAgent = navigator.userAgent.toLowerCase()
	if (userAgent.includes("win64")) {
		return "win64"
	}
	if (userAgent.includes("windows")) {
		return "win"
	}
	if (userAgent.includes("linux")) {
		return "linux"
	}
	return "mac"
}

const resolvePlatform = (): Platform => {
	if (typeof navigator === "undefined") {
		return "other"
	}

	const platformString =
		hasUserAgentData(navigator) && typeof navigator.userAgentData?.platform === "string"
			? navigator.userAgentData.platform
			: (navigator.platform ?? "")
	const normalized = platformString.toLowerCase()
	const ua = navigator.userAgent.toLowerCase()

	if (normalized.includes("cros") || ua.includes("cros")) {
		return "chromeos"
	}
	if (normalized.includes("win") || ua.includes("windows")) {
		return "windows"
	}
	if (normalized.includes("mac") || ua.includes("mac os")) {
		return "mac"
	}
	if (normalized.includes("linux") || ua.includes("linux") || ua.includes("x11")) {
		return "linux"
	}
	return "other"
}

export const parseChromeMajorVersion = (version?: string | null) => {
	if (!version) {
		return null
	}
	const major = Number.parseInt(version.split(".")[0] ?? "", 10)
	return Number.isNaN(major) ? null : major
}

const extractLatestStableVersion = (value: unknown): string | null => {
	if (!isRecord(value)) {
		return null
	}

	const versionsValue = value.versions
	if (!Array.isArray(versionsValue)) {
		return null
	}

	for (const entry of versionsValue) {
		if (!isRecord(entry)) {
			continue
		}
		const versionValue = entry.version
		if (typeof versionValue === "string") {
			return versionValue
		}
	}

	return null
}

const buildLatestStableVersionUrl = (platform: ChromePlatformSegment) => {
	return `${VERSION_HISTORY_API_BASE}/${platform}/channels/stable/versions`
}

export const fetchLatestStableChromeMajorVersion = async (
	signal?: AbortSignal,
): Promise<number | null> => {
	if (typeof fetch !== "function") {
		return null
	}

	const platform = detectChromePlatform()
	try {
		const response = await fetch(buildLatestStableVersionUrl(platform), { signal })
		if (!response.ok) {
			return null
		}

		const data: unknown = await response.json()
		const latestVersion = extractLatestStableVersion(data)
		return parseChromeMajorVersion(latestVersion)
	} catch (error) {
		const hasAbortName =
			(error instanceof DOMException && error.name === "AbortError") ||
			(error instanceof Error && error.name === "AbortError") ||
			(isRecord(error) && typeof error.name === "string" && error.name === "AbortError")
		if (hasAbortName) {
			return null
		}
		console.warn("Failed to retrieve latest Chrome version", error)
		return null
	}
}

interface UpgradeInstruction {
	title: string
	description: string
}

const getUpgradeInstructions = (
	platform: Platform,
	t: (key: string) => string,
): UpgradeInstruction[] => {
	switch (platform) {
		case "windows":
			return [
				{
					title: t("browserCompatibilityGuard.upgradeTitleWindows"),
					description: t("browserCompatibilityGuard.upgradeDescriptionWindows"),
				},
			]
		case "linux":
			return [
				{
					title: t("browserCompatibilityGuard.upgradeTitleLinux"),
					description: t("browserCompatibilityGuard.upgradeDescriptionLinux"),
				},
			]
		case "mac":
		default:
			return [
				{
					title: t("browserCompatibilityGuard.upgradeTitleMac"),
					description: t("browserCompatibilityGuard.upgradeDescriptionMac"),
				},
			]
	}
}

interface OldChromeWarningOptions {
	browserName: string
	browserVersion: string | null
	browserMajorVersion?: number | null
}

export const useOldChromeWarning = ({
	browserName,
	browserVersion,
	browserMajorVersion,
}: OldChromeWarningOptions) => {
	// Playwright/automation runs use outdated Chromium; skip the warning to avoid blocking E2E flows
	const skipWarning = isAutomatedBrowser()
	const majorVersion = useMemo(
		() => browserMajorVersion ?? parseChromeMajorVersion(browserVersion ?? null),
		[browserMajorVersion, browserVersion],
	)

	const [minimumChromeVersion, setMinimumChromeVersion] = useState(FALLBACK_MIN_CHROME_VERSION)
	const [isDismissed, setIsDismissed] = useState(false)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [fullBrowserVersion, setFullBrowserVersion] = useState(browserVersion ?? "Unknown")
	const { t } = useTranslation("components")

	const isChrome = browserName === "Chrome"
	const platform = useMemo(resolvePlatform, [])
	const upgradeInstructions = useMemo(() => getUpgradeInstructions(platform, t), [platform, t])
	const instructionsComponent = useMemo<ReactNode | undefined>(() => {
		if (upgradeInstructions.length === 0) {
			return undefined
		}
		return (
			<div className="mt-3 space-y-2 text-sm text-base-200">
				{upgradeInstructions.map((instruction) => (
					<p key={instruction.title}>{instruction.description}</p>
				))}
			</div>
		)
	}, [upgradeInstructions])

	useEffect(() => {
		if (!isChrome || skipWarning) {
			return
		}

		const controller = new AbortController()
		let cancelled = false

		void fetchLatestStableChromeMajorVersion(controller.signal).then((latestMajor) => {
			if (cancelled || latestMajor === null) {
				return
			}
			setMinimumChromeVersion((current) => {
				const baseline = Math.max(current, FALLBACK_MIN_CHROME_VERSION)
				if (majorVersion === null) {
					return baseline
				}
				if (latestMajor > majorVersion + 1) {
					return Math.max(baseline, latestMajor)
				}
				return baseline
			})
		})

		return () => {
			cancelled = true
			controller.abort()
		}
	}, [isChrome, majorVersion, skipWarning])

	useEffect(() => {
		if (browserName !== "Chrome" || typeof navigator === "undefined" || skipWarning) {
			return
		}

		const userAgentMatch = navigator.userAgent.match(/(?:Chrome|Chromium|CriOS)\/([\d.]+)/)
		const initialVersion = userAgentMatch?.[1] ?? browserVersion ?? "Unknown"
		setFullBrowserVersion((current) => (current === initialVersion ? current : initialVersion))

		const uaData = hasUserAgentData(navigator) ? navigator.userAgentData : undefined
		if (!uaData) {
			return
		}

		let cancelled = false

		const applyVersion = (version?: string) => {
			if (version && version !== initialVersion && !cancelled) {
				setFullBrowserVersion(version)
			}
		}

		const lowEntropyMatch = uaData.brands?.find(
			(entry) => entry.brand === "Google Chrome" || entry.brand === "Chromium",
		)?.version
		if (lowEntropyMatch) {
			applyVersion(lowEntropyMatch)
		}

		const highEntropyLoader = uaData.getHighEntropyValues
		if (!highEntropyLoader) {
			return () => {
				cancelled = true
			}
		}

		void highEntropyLoader(["fullVersionList"])
			.then((values) => {
				if (cancelled) {
					return
				}
				const match = values.fullVersionList?.find(
					(entry) => entry.brand === "Google Chrome" || entry.brand === "Chromium",
				)
				applyVersion(match?.version)
			})
			.catch(() => {
				// Ignore failures (e.g., not a secure context)
			})

		return () => {
			cancelled = true
		}
	}, [browserName, browserVersion, skipWarning])

	const shouldShowDialog =
		isChrome &&
		!skipWarning &&
		majorVersion !== null &&
		majorVersion < minimumChromeVersion &&
		!isDismissed

	useEffect(() => {
		if (!shouldShowDialog || dialogOpen || majorVersion === null) {
			return
		}

		pushDialog(ConfirmDialog, {
			title: t("browserCompatibilityGuard.updateChromeTitle"),
			message: t("browserCompatibilityGuard.updateChromeMessage", {
				version: fullBrowserVersion,
				minimumVersion: minimumChromeVersion,
			}),
			embeddedComponent: instructionsComponent,
			primary: {
				title: t("browserCompatibilityGuard.dismissButton"),
				handler: () => {
					setIsDismissed(true)
					setDialogOpen(false)
				},
				trackingLabel: "dismiss_old_chrome_warning",
			},
			trackingViewName: "old_chrome_version_dialog",
			canClose: false,
			onFinish: () => {
				setDialogOpen(false)
			},
		})
		setDialogOpen(true)
	}, [
		shouldShowDialog,
		dialogOpen,
		majorVersion,
		minimumChromeVersion,
		fullBrowserVersion,
		instructionsComponent,
	])
}
